import { useSyncExternalStore } from "react";
import type { FactoryRuntimeSettings, FactorySettingScalar } from "../models/factory-integration-settings.model";
import { factoryIntegrationSettingsRepo } from "../repos/factory-integration-settings.repo";

export const GENERAL_SETTING_KEYS = ["companyName", "factoryName", "brandName", "taxCode", "allowedArea", "defaultArea", "defaultWaterMeterName", "defaultWaterMeterType", "allowEditUsername", "androidUrl", "iosUrl", "apkUrl", "apkLabel"] as const;
const RUNTIME_SETTING_KEYS = [...GENERAL_SETTING_KEYS, "aiDeepseekEnabled", "aiCerebrasEnabled", "aiDeepseekModel", "aiCerebrasModel"] as const;
export type RuntimeSettingKey = typeof RUNTIME_SETTING_KEYS[number];
interface RuntimeSnapshot { factoryId: string | null; environment: string | null; settings: Partial<Record<RuntimeSettingKey, FactorySettingScalar>>; contextId: number; }

/** In-memory only. Each login/factory change synchronously discards the previous tenant's data. */
export class FactoryRuntimeSettingsStore {
  private listeners = new Set<() => void>();
  private token: string | null = null;
  private request = 0;
  private snapshot: RuntimeSnapshot = { factoryId: null, environment: null, settings: {}, contextId: 0 };
  constructor(private readonly load: (token: string) => Promise<FactoryRuntimeSettings>) {}
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private publish(next: RuntimeSnapshot) { this.snapshot = next; this.listeners.forEach(listener => listener()); }
  async setSession(factoryId: string | null, token: string | null) {
    if (factoryId === this.snapshot.factoryId && token === this.token) return;
    this.token = token;
    const version = ++this.request;
    this.publish({ factoryId, environment: null, settings: {}, contextId: this.snapshot.contextId + 1 });
    if (!factoryId || !token) return;
    await this.loadForSession(factoryId, token, version);
  }
  private async loadForSession(factoryId: string, token: string, version: number) {
    try {
      const result = await this.load(token);
      if (version !== this.request || result.factoryId !== factoryId) return;
      const settings: RuntimeSnapshot["settings"] = {};
      RUNTIME_SETTING_KEYS.forEach(key => {
        const value = result.settings?.[key];
        const boolean = key === "allowEditUsername" || key === "aiDeepseekEnabled" || key === "aiCerebrasEnabled";
        if (boolean ? typeof value === "boolean" : typeof value === "string") settings[key] = value;
      });
      this.publish({ ...this.snapshot, environment: result.environment, settings });
    } catch { /* Older APIs and unavailable settings retain the existing build defaults. */ }
  }
  async refresh() {
    const factoryId = this.snapshot.factoryId;
    const token = this.token;
    if (factoryId && token) await this.loadForSession(factoryId, token, ++this.request);
  }
  string(key: RuntimeSettingKey, fallback = "") { const value = this.snapshot.settings[key]; return typeof value === "string" ? value : fallback; }
  boolean(key: RuntimeSettingKey, fallback = false) { const value = this.snapshot.settings[key]; return typeof value === "boolean" ? value : fallback; }
}

export const factoryRuntimeSettings = new FactoryRuntimeSettingsStore(token => factoryIntegrationSettingsRepo.getRuntime(token));
export const useFactoryRuntimeSettings = () => useSyncExternalStore(factoryRuntimeSettings.subscribe, factoryRuntimeSettings.getSnapshot, factoryRuntimeSettings.getSnapshot);
